"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import CollapsibleText from "@/components/ui/collapsible-text";
import { formatDate } from "@/lib/format-data";
import { formatMeasurementValue } from "@/lib/measurement-calculations";
import { cx } from "@/lib/tailwind-utils";
import type { MeasurementField, MeasurementValueSummary, PatientMeasurementsResponse } from "@/types/measurement";
import { SectionLoadError } from "./section-load-error";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { measurementQueryOptions } from "@/query/patient/measurements";
import { measurementFieldQueryOptions } from "@/query/settings/measurement-fields";
const MAX_FIELDS = 8;
function valueTime(value: MeasurementValueSummary): number {
  return new Date(value.measurementDate).getTime();
}

/** The latest reading of each measurement, most recently recorded first. */
export default function VitalsSection(t0) {
  const $ = _c(23);
  const {
    patientId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...measurementFieldQueryOptions.list(),
      select: _temp
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const fieldsQuery = useQuery(t1);
  let t2;
  if ($[1] !== patientId) {
    t2 = measurementQueryOptions.patient(patientId);
    $[1] = patientId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = {
      ...t2,
      select: _temp2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const valuesQuery = useQuery(t3);
  const fields = fieldsQuery.data;
  const data_1 = valuesQuery.data;
  data_1?.values;
  let t4;
  if ($[5] !== data_1?.values) {
    const byField = {};
    for (const value of data_1?.values ?? []) {
      if (value.isLabSourced) {
        continue;
      }
      const existing = byField[value.fieldId];
      if (!existing || valueTime(value) > valueTime(existing)) {
        byField[value.fieldId] = value;
      }
    }
    t4 = Object.values(byField).sort(_temp3);
    $[5] = data_1?.values;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const latest = t4;
  if (fieldsQuery.isPending || valuesQuery.isPending) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <PatientSidebarSectionSkeleton />;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  if (!fields || !data_1 || fieldsQuery.isError || valuesQuery.isError) {
    let t5;
    if ($[8] !== data_1 || $[9] !== fields || $[10] !== fieldsQuery || $[11] !== valuesQuery) {
      t5 = () => {
        if (!fields || fieldsQuery.isError) {
          fieldsQuery.refetch();
        }
        if (!data_1 || valuesQuery.isError) {
          valuesQuery.refetch();
        }
      };
      $[8] = data_1;
      $[9] = fields;
      $[10] = fieldsQuery;
      $[11] = valuesQuery;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    const t6 = fieldsQuery.isRefetching || valuesQuery.isRefetching;
    let t7;
    if ($[13] !== t5 || $[14] !== t6) {
      t7 = <SectionLoadError label="vitals" onRetry={t5} isRetrying={t6} />;
      $[13] = t5;
      $[14] = t6;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  let t5;
  let t6;
  if ($[16] !== fields || $[17] !== latest) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t7;
      if ($[20] !== fields) {
        t7 = value_0 => {
          const field = fields.find(f => f.id === value_0.fieldId);
          return field && !field.isArchived ? [{
            value: value_0,
            field
          }] : [];
        };
        $[20] = fields;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      const rows = latest.flatMap(t7);
      if (rows.length === 0) {
        let t8;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <p className="text-caption text-muted-foreground">None recorded</p>;
          $[22] = t8;
        } else {
          t8 = $[22];
        }
        t6 = t8;
        break bb0;
      }
      t5 = <div><VitalsGrid rows={rows.slice(0, MAX_FIELDS)} />{rows.length > MAX_FIELDS && <CollapsibleText className="mt-2" text={`Show all ${rows.length}`} collapsedText="Show less" textClassName="text-caption"><VitalsGrid rows={rows.slice(MAX_FIELDS)} className="mt-1.5" /></CollapsibleText>}</div>;
    }
    $[16] = fields;
    $[17] = latest;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  return t5;
}
function _temp3(a, b) {
  return valueTime(b) - valueTime(a);
}
function _temp2(data_0) {
  return data_0;
}
function _temp(data) {
  return data;
}
function VitalsGrid(t0) {
  const $ = _c(7);
  const {
    rows,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("grid grid-cols-2 gap-1.5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== rows) {
    t2 = rows.map(_temp4);
    $[2] = rows;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp4(t0) {
  const {
    value,
    field
  } = t0;
  return <div key={value.id} className="min-w-0 rounded-md bg-background-secondary px-2 py-1.5"><p className="truncate text-caption text-foreground-secondary">{field.name}</p><p className="truncate text-body font-semibold tabular-nums text-foreground">{formatMeasurementValue(value, field)}</p><p className="text-caption text-foreground-muted">{formatDate(value.measurementDate)}</p></div>;
}